/**
 * CAT 自适应推题引擎
 *
 * θ 更新公式与 irtScore.js 完全一致；
 * 选题标准：Fisher Information 最大化，ties 用题型/科目均衡打破。
 */

const DIFF_PARAMS = {
  'J1+': { b: 1.8, a: 1.5 },
  'J1':  { b: 0.9, a: 1.2 },
  'J2':  { b: 0.0, a: 1.0 },
  'J3':  { b: -1.5, a: 0.8 },
};

function pCorrect(theta, b, a) {
  return 1 / (1 + Math.exp(-a * (theta - b)));
}

function fisherInfo(theta, b, a) {
  const p = pCorrect(theta, b, a);
  return a * a * p * (1 - p);
}

/** 初始 IRT 状态 */
export function initialIrtState() {
  return { theta: 0, hardStreak: 0, weightMult: 1.0 };
}

/**
 * 根据答题结果更新 θ（精确复现 irtScore.js 逻辑）
 * @param {Object} state  { theta, hardStreak, weightMult }
 * @param {Object} question
 * @param {boolean} isCorrect
 * @returns {Object} 新状态
 */
export function updateTheta(state, question, isCorrect) {
  const params = DIFF_PARAMS[question.difficulty] || DIFF_PARAMS['J2'];
  const { b, a } = params;
  let { theta, hardStreak, weightMult } = state;

  const isHard = question.difficulty === 'J1+' || question.difficulty === 'J1';
  const isEasy = question.difficulty === 'J3';

  if (isCorrect) {
    if (isHard) {
      hardStreak++;
      weightMult = Math.min(1.7, 1.0 + hardStreak * 0.12);
    } else {
      hardStreak = 0;
      weightMult = 1.0;
    }
    theta += a * pCorrect(theta, b, a) * weightMult;
  } else {
    hardStreak = 0;
    weightMult = 1.0;
    if (isEasy)                     theta -= a * 2.8;
    else if (question.difficulty === 'J2') theta -= a * 1.4;
    else                            theta -= a * 0.5;
  }

  return { theta, hardStreak, weightMult };
}

/**
 * 从题库中选出当前 θ 下 Fisher Information 最大的下一题
 * 同信息量内（5% 内）优先覆盖未出现的科目，其次补足最少的题型。
 * @param {number} theta
 * @param {Array} pool       全部可用题目
 * @param {Set}   answeredIds 已答题 id 集合
 */
export function selectNext(theta, pool, answeredIds) {
  const remaining = pool.filter(q => !answeredIds.has(q.id));
  if (remaining.length === 0) return null;

  const withInfo = remaining.map(q => {
    const { b, a } = DIFF_PARAMS[q.difficulty] || DIFF_PARAMS['J2'];
    return { q, info: fisherInfo(theta, b, a) };
  });

  const maxInfo = Math.max(...withInfo.map(x => x.info));
  const candidates = withInfo.filter(x => x.info >= maxInfo * 0.95);
  if (candidates.length === 1) return candidates[0].q;

  // 已答题的科目与题型计数
  const sectionCounts = { reading: 0, listening: 0, listeningReading: 0 };
  const typeCounts    = { grammar: 0, vocab: 0, expression: 0 };
  pool.filter(q => answeredIds.has(q.id)).forEach(q => {
    sectionCounts[q.section] = (sectionCounts[q.section] || 0) + 1;
    typeCounts[q.type]       = (typeCounts[q.type]    || 0) + 1;
  });

  // 答题数 ≥ 3 后，优先补充空白科目
  if (answeredIds.size >= 3) {
    const missing = Object.keys(sectionCounts).filter(s => sectionCounts[s] === 0);
    if (missing.length > 0) {
      const sectionCands = candidates.filter(x => missing.includes(x.q.section));
      if (sectionCands.length > 0) {
        sectionCands.sort((a, b) => (typeCounts[a.q.type] || 0) - (typeCounts[b.q.type] || 0));
        return sectionCands[0].q;
      }
    }
  }

  // 次选：补足最少的题型
  candidates.sort((a, b) => (typeCounts[a.q.type] || 0) - (typeCounts[b.q.type] || 0));
  return candidates[0].q;
}

/**
 * 标准误（测量精度）：SE = 1 / √ΣI_i(θ)
 * @param {number} theta
 * @param {Array}  questionHistory  已答题目对象数组
 */
export function getStdError(theta, questionHistory) {
  const totalInfo = questionHistory.reduce((sum, q) => {
    const { b, a } = DIFF_PARAMS[q.difficulty] || DIFF_PARAMS['J2'];
    return sum + fisherInfo(theta, b, a);
  }, 0);
  return totalInfo > 0 ? 1 / Math.sqrt(totalInfo) : Infinity;
}

/** θ → BJT 分数（200–700），与 irtScore.js 映射一致 */
export function thetaToScore(theta) {
  const clamped = Math.max(-4, Math.min(4, theta));
  return Math.max(200, Math.min(700, Math.round(200 + ((clamped + 4) / 8) * 500)));
}
